import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { BellRing, BadgeCheck, HeartHandshake, CheckCircle2, Clock } from "lucide-react";

const money = (n, c) => `${(Math.round((n || 0) * 100) / 100).toLocaleString("fr-FR")} ${c === "EUR" ? "€" : c === "USD" ? "$" : c || ""}`;
const when = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "");

const Stat = ({ Icon, label, s, cur, testid }) => (
  <div className="rounded-xl p-3" style={{ background: "var(--yalola-surface-2, rgba(0,0,0,0.03))" }} data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[11px] uppercase font-bold tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>
      <Icon size={13} /> {label}
    </div>
    <div className="mt-1 flex items-baseline gap-2 flex-wrap">
      <span className="text-2xl font-bold font-display tabular-nums" data-testid={`${testid}-sent`}>{s.sent}</span>
      <span className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>rappel{s.sent > 1 ? "s" : ""} envoyé{s.sent > 1 ? "s" : ""}</span>
    </div>
    <div className="text-xs mt-0.5" style={{ color: s.converted ? "#065F46" : "var(--yalola-text-muted)" }} data-testid={`${testid}-converted`}>
      → {s.converted} paiement{s.converted > 1 ? "s" : ""} après rappel ({s.rate} %){s.amount ? ` · ${money(s.amount, cur)}` : ""}
    </div>
  </div>
);

// Vue d'ensemble : rappels doux (page Stripe « Vérifié » / fenêtre de don fermées sans payer) envoyés sur 30 j et paiements obtenus après.
export const RemindersCard = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/finance/reminders/summary", { params: { days: 30 } }).then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);
  if (!data) return null;
  const total = data.verification.sent + data.donations.sent;
  return (
    <div className="yalola-card p-4" style={{ borderLeft: "4px solid #2D5A4C" }} data-testid="admin-reminders-card" data-total={total}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <BellRing size={16} style={{ color: "#2D5A4C" }} />
          <div>
            <div className="font-bold font-display text-sm">Relances paiement · {data.days} derniers jours</div>
            <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
              Rappel doux automatique (1 seul, entre 2 h et 7 j) à qui a ouvert la page Stripe « Yalola Vérifié » ou une fenêtre de don sans finaliser — et ce que ça a rapporté.
            </div>
          </div>
        </div>
        <Link to="/admin/transactions?focus=journal" className="text-xs underline" style={{ color: "var(--yalola-primary)" }} data-testid="admin-reminders-journal">Voir le journal</Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
        <Stat Icon={BadgeCheck} label="Yalola Vérifié" s={data.verification} cur={data.currency} testid="reminders-verification" />
        <Stat Icon={HeartHandshake} label="Dons" s={data.donations} cur={data.currency} testid="reminders-donations" />
      </div>
      {total === 0 ? (
        <p className="text-xs mt-3" style={{ color: "var(--yalola-text-muted)" }} data-testid="admin-reminders-empty">Aucun rappel envoyé sur la période : personne n'a abandonné un paiement.</p>
      ) : (
        <div className="mt-3 divide-y text-xs" style={{ borderColor: "var(--yalola-border)" }} data-testid="admin-reminders-recent">
          {/* Vue d'ensemble : seulement les 2 dernières relances — le reste est dans le journal (Comptabilité). */}
          {data.recent.slice(0, 2).map((r, i) => (
            <div key={`${r.user_id}-${r.sent_at}-${i}`} className="py-1.5 flex items-center gap-2 flex-wrap" data-testid={`reminder-row-${i}`}>
              {r.kind === "verification" ? <BadgeCheck size={12} style={{ color: "#2D5A4C" }} /> : <HeartHandshake size={12} style={{ color: "#B45309" }} />}
              <Link to={`/profile/${r.user_id}`} className="font-semibold hover:underline">{r.name}</Link>
              <span style={{ color: "var(--yalola-text-muted)" }}>
                {r.kind === "verification" ? "badge Vérifié" : `don${r.receiver ? ` pour ${r.receiver}` : ""}`} · relancé le {when(r.sent_at)}
              </span>
              {r.paid ? (
                <span className="chip text-[10px] inline-flex items-center gap-1" style={{ background: "#D1FAE5", color: "#065F46" }} data-testid={`reminder-row-paid-${i}`}>
                  <CheckCircle2 size={10} /> payé le {when(r.paid_at)}{r.amount ? ` · ${money(r.amount, r.currency || data.currency)}` : ""}
                </span>
              ) : (
                <span className="chip text-[10px] inline-flex items-center gap-1" style={{ background: "#F3F4F6", color: "#6B7280" }} data-testid={`reminder-row-pending-${i}`}>
                  <Clock size={10} /> pas encore
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
